// @flow
import * as React from 'react'
import { withRouter } from 'next/router'
import { connect } from 'react-redux'
import type { AppStateType } from '@reducers/appstate'
import {
  generateMarketingLandingPageSlugs,
  fetchMarketingLandingPage
} from '@services/contentfulMarketingLandingPages'
import type {
  MarektingLandingPageSlugsType,
  MarketingLandingPageType
} from '@services/contentfulMarketingLandingPages'
import { GlobalStyle } from '@components/shared'
import { colors } from '@components/shared/colors'
import HtmlHead from '@components/HtmlHead'
import TopNav from '@components/TopNav'
import TopNavMobile from '@components/TopNavMobile'
import Footer from '@components/Footer'
import CarouselAndDriveToDownloadSection from '@components/marketingLandingPage/CarouselAndDriveToDownloadSection'
import BottomBanner from '@components/marketingLandingPage/BottomBanner'
import ContentBlocks from '@components/marketingLandingPage/ContentBlocks'
import ConversionPaywallModal from '@components/subscribe/ConversionPaywallModal'
import { dismissIntercom } from '@services/intercom'
import { ANNUAL_PRICE_STRING } from '../../helpers/subscriptionHelpers'
type StaticPropsType = {
  landingPage?: MarketingLandingPageType | null,
  slug?: string
}

type PropsType = {
  landingPage?: MarketingLandingPageType | null,
  slug?: string,
  isSignedIn: boolean
}

type ContextType = {
  params: {
    slug: string
  }
}

function MarketingLandingPage ({
  landingPage,
  slug,
  isSignedIn
}: PropsType): React.Node {
  if (!landingPage) {
    return <div>loading</div>
  }

  const isIntercomVisible = typeof window !== 'undefined' && window.Intercom
  React.useEffect((): void => {
    if (isIntercomVisible) {
      dismissIntercom()
    }
  }, [isIntercomVisible])

  const [subscribeUrl, setSubscribeUrl] = React.useState(undefined)
  React.useEffect((): void => {
    if (slug === 'kenna' || slug === 'mairsyy' || slug === 'amanda' || slug === 'rashad' || slug === 'becky') {
      // $FlowFixMe
      const url = landingPage.contentBlocks?.[1]?.cta?.cta?.ctaUrl
      if (url && url.includes('subscribe')) {
        setSubscribeUrl(url)
      }
    }
  }, [slug])

  const [isMiniPlayerVisible, setIsMiniPlayerVisible] = React.useState(true)
  const footerRef = React.useRef()
  const mainRef = React.useRef()

  if (landingPage.audioPlayer && !!landingPage.navigationShowFooterOnPage) {
    React.useEffect((): any => {
      window.addEventListener('scroll', handleScroll)
      return (): any => window.removeEventListener('scroll', handleScroll)
    })

    const handleScroll = (): void => {
      let mainHeight = 0
      const miniPlayerHeight = 90

      if (mainRef.current) {
        const { clientHeight } = mainRef.current
        mainHeight = clientHeight
      }

      if (mainHeight - window.innerHeight + miniPlayerHeight > window.scrollY) {
        if (!isMiniPlayerVisible) setIsMiniPlayerVisible(true)
      } else {
        if (isMiniPlayerVisible) setIsMiniPlayerVisible(false)
      }
    }
  }
  let metaDescriptionText = 'Spicy audiobooks and more, featuring your favorite tropes and irresistible characters. Crafted by our team of professional writers and top-tier narrators.'
  let metaHeadText = 'Dipsea | Short and Sexy Audio Stories'
  if (landingPage.metaTitle) {
    metaHeadText = `Dipsea | ${landingPage.metaTitle}`
  }
  if (landingPage.metaDescription) {
    metaDescriptionText = landingPage.metaDescription
  }

  return (
    <>
      <GlobalStyle backgroundColor={colors.midnightBlue} />
      <HtmlHead title={metaHeadText} description={metaDescriptionText} includeStripe={true}/>

      {landingPage.navigationShowDefaultTopNav
        ? (
        <TopNav
          backgroundColor={colors.midnightBlue95}
          isAssetLoaded={true}
          isPromoModal={landingPage.navigationSubscribeButtonOpensModal}
        />
          )
        : (
        <TopNav
          backgroundColor={colors.midnightBlue95}
          isAssetLoaded={true}
          isDipseaLogoOnly
        />
          )}

      {landingPage.navigationShowDefaultTopNav && (
        <TopNavMobile
          backgroundColor={colors.midnightBlue95}
          isAssetLoaded
          isPromoModal={landingPage.navigationSubscribeButtonOpensModal}
        />
      )}

      <main ref={mainRef}>
        <ContentBlocks
          contentBlocks={landingPage.contentBlocks}
          isFooterVisible={landingPage.navigationShowFooterOnPage}
          hasBanner={!!landingPage.audioPlayer}
        />
        <CarouselAndDriveToDownloadSection
          pageId={landingPage.id}
          carouselType={landingPage.bottomCarousel}
        />
      </main>

      <div ref={footerRef}>
        {landingPage.navigationShowFooterOnPage && (
          <Footer
            backgroundColor={colors.midnightBlue}
            isPromoModal={landingPage.navigationSubscribeButtonOpensModal}
            subscribeUrl={subscribeUrl}
          />
        )}
      </div>

      <ConversionPaywallModal
        subtype='annual'
        defaultSignIn
        subscriptionOfferCta='TRY 7 DAYS FREE'
        subscriptionOfferTitle='7 Day Free Trial'
        subscriptionOfferSubtitle={`Annual subscription of ${ANNUAL_PRICE_STRING} USD billed at trial end`}
        subscriptionOfferSubtext={`Your card will be charged a sum of ${ANNUAL_PRICE_STRING} USD in one week for the first year of unlimited access to Dipsea. You must cancel your subscription before the end of your free trial if you do not want to be charged. Your annual subscription will auto-renew for ${ANNUAL_PRICE_STRING} USD in exactly one year and each year thereafter until you cancel.`}
        isFullScreen
        pageSlugForPromoEvent={slug}
      />

      {landingPage.audioPlayer && (
        <BottomBanner
          pageId={landingPage.id}
          title={landingPage.audioPlayer.title}
          audioUrl={landingPage.audioPlayer.audioUrl}
          backgroundColorText={landingPage.audioPlayer.backgroundColor}
          isMiniPlayerVisible={isMiniPlayerVisible}
          cta={landingPage.audioPlayer.cta}
        />
      )}
    </>
  )
}

export async function getStaticProps (
  context: ContextType
): Promise<{ props: StaticPropsType }> {
  const landingPage = await fetchMarketingLandingPage(context.params.slug)
  return {
    props: {
      landingPage,
      slug: context.params.slug
    }
  }
}

export async function getStaticPaths (): Promise<MarektingLandingPageSlugsType> {
  const paths = await generateMarketingLandingPageSlugs()
  return paths
}

const mapStateToProps = (state: AppStateType, ownProps: PropsType): any => {
  return {
    ...ownProps,
    isSignedIn: !!(state.user || {}).id
  }
}

export default withRouter(connect(mapStateToProps, {})(MarketingLandingPage))
